import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import classNames from 'classnames';
import DatePicker from 'react-datepicker';
import { injectIntl } from 'react-intl';
import { format } from 'date-fns';
import {
  Alert,
  ButtonToolbar,
  Col,
  FormGroup,
  Row,
} from 'react-bootstrap';
import Select from 'react-select';
import Button from '../ui/Button';
import { MetadataSearchTerm, SearchTerm } from '../../utils/searchUtils';
import { retrieveFilteredUMOs } from '../../actions/umoGallery';
import appConfig from '../../appConfig';
import CloseIcon from '../../../icons/sub-menu-and-common-icons/close-icon.svg';
import { SelectStyles } from '../../../style/components/react-select';
import { DATE_FORMAT } from '../../constants/session';

const DEFAULT_FROM_DATE = '01-Jan-0000';
const DEFAULT_TO_DATE = '01-Jan-5000';
const DEFAULT_FROM_SIZE = 0;
const DEFAULT_TO_SIZE = 10737418240000;

// left for debug purposes, not in use anymore
// function CustomDatePickerInput(props) {
//   return (
//     <FormControl
//       className="umo-filter__date-input"
//       type="text"
//       placeholder={props.placeholder}
//       value={props.value}
//       onClick={props.onClick}
//     />
//   );
// }

/**
 * Bit of info regarding the way this works. The fields to use for the filter is
 * defined in appConfig. At the moment there is no way to retrieve from Piction
 * specific fields so a complete list of metatags is retrieved from Piction,
 * and then the required ones are used here. The session store has already got
 * the list of metatags so that is used to populate this component.
 */
function UMOFilter(props) {
  const [filterByFieldsList, setFilterByList] = useState(new List());
  const [error, setError] = useState(null);
  const dateFormat = useMemo(() => appConfig.dateFormat || DATE_FORMAT, []);

  let newFilterByFieldsList = new List();

  useEffect(() => {
    setFilterFields(true);
  }, [props.metatags]);

  function setFilterFields(useCurValues) {
    props.metatags.forEach((metatagList) => {
      if (
        metatagList instanceof List &&
        appConfig.umoGallery.filterByOptions &&
        Array.isArray(appConfig.umoGallery.filterByOptions)
      ) {
        appConfig.umoGallery.filterByOptions.map((option, i) => {
          switch (option.type) {
            case 'metatag': {
              // find the metatag in the metatags list
              const metatagMap = metatagList.find(
                (tagItem) =>
                  tagItem.getIn(['TAG_NAME', 0]) === option.name &&
                  tagItem.getIn(['CATEGORY', 0]) === option.category
              );
              if (metatagMap) {
                // get list of options for the drop down
                let filterOptions = metatagMap && metatagMap.get('l');

                // filterByItem is the item in the store that matches the current
                // metatag. used later to set the current value for the filter
                const filterByItem =
                  useCurValues &&
                  props.filterByList &&
                  props.filterByList.find(
                    (searchTerm) =>
                      searchTerm instanceof MetadataSearchTerm &&
                      searchTerm.category ===
                        metatagMap.getIn(['CATEGORY', 0]) &&
                      searchTerm.name === metatagMap.getIn(['TAG_NAME', 0])
                  );

                if (filterOptions instanceof List && filterOptions.size > 0) {
                  filterOptions = filterOptions.map((optionData, j) => {
                    if (typeof optionData !== 'string') {
                      const val = optionData.get('_');
                      if (val) return val;
                    }
                    return optionData;
                  });

                  // only add to list if the field doesn't already exist
                  const itemLabel =
                    metatagMap.getIn(['DISPLAY_VALUE', 0]) ||
                    metatagMap.getIn(['TAG_NAME', 0]);
                  if (
                    !newFilterByFieldsList.find(
                      (item) => item.get('label') === itemLabel
                    )
                  ) {
                    newFilterByFieldsList = newFilterByFieldsList.push(
                      new Map({
                        searchTerm: new MetadataSearchTerm(
                          metatagMap.getIn(['CATEGORY', 0]),
                          metatagMap.getIn(['TAG_NAME', 0]),
                          filterByItem && filterByItem.get('value')
                        ),
                        type: 'metatag',
                        label:
                          metatagMap.getIn(['DISPLAY_VALUE', 0]) ||
                          metatagMap.getIn(['TAG_NAME', 0]),
                        filterOptions,
                      })
                    );
                  }
                } else {
                  console.warn(
                    `${metatagMap.getIn([
                      'TAG_NAME',
                      0,
                    ])} doesn't have a list of values for the filter`,
                    metatagMap.toJS()
                  );
                }
              }
              break;
            }
            case 'dateRange': {
              const filterByItem =
                useCurValues &&
                props.filterByList &&
                props.filterByList.find(
                  (searchTerm) =>
                    searchTerm instanceof SearchTerm &&
                    searchTerm.keyword === 'DATE_RANGE'
                );

              let fromDate;
              let toDate;

              if (filterByItem && filterByItem.value) {
                const dateSplit = filterByItem.value.split(',');

                // conditional stops the default date range from showing
                fromDate =
                  dateSplit[0] !== DEFAULT_FROM_DATE
                    ? format(dateSplit[0], dateFormat)
                    : undefined;

                toDate =
                  dateSplit[1] !== DEFAULT_TO_DATE
                    ? format(dateSplit[1], dateFormat)
                    : undefined;
              }

              // only add to list if the field doesn't already exist
              if (
                !newFilterByFieldsList.find(
                  (item) => item.get('type') === 'dateRange'
                )
              ) {
                newFilterByFieldsList = newFilterByFieldsList.push(
                  new Map({
                    type: 'dateRange',
                    label: props.intl.formatMessage({
                      id: 'searchResults.filter.date',
                    }),
                    fromDate,
                    toDate,
                  })
                );
              }
              break;
            }
            case 'sizeRange': {
              const filterByItem =
                useCurValues &&
                props.filterByList &&
                props.filterByList.find(
                  (searchTerm) =>
                    searchTerm instanceof SearchTerm &&
                    searchTerm.keyword === 'NUMBER_RANGE'
                );

              let fromSize;
              let toSize;

              if (filterByItem && filterByItem.value) {
                const sizeSplit = filterByItem.value.split(',');

                // conditional stops the default size range from showing
                fromSize = sizeSplit[1];
                toSize = sizeSplit[2];
              }
              // only add to list if the field doesn't already exist
              if (
                !newFilterByFieldsList.find(
                  (item) => item.get('type') === 'sizeRange'
                )
              ) {
                newFilterByFieldsList = newFilterByFieldsList.push(
                  new Map({
                    type: 'sizeRange',
                    label: 'File Size (in MB)',
                    fromSize,
                    toSize,
                  })
                );
              }
              break;
            }
            default:
              break;
          }
        });
        setFilterByList(newFilterByFieldsList);
        setError(null);
      }
    });
  }

  function onMetatagChange(filterIndex, value) {
    setFilterByList(
      filterByFieldsList.setIn(
        [filterIndex, 'searchTerm', 'value'],
        value.value
      )
    );
  }

  function onDateChange(date, e, filterIndex, isFromDate) {
    setFilterByList(
      filterByFieldsList.setIn(
        [filterIndex, isFromDate ? 'fromDate' : 'toDate'],
        date ? format(date, dateFormat) : '',
      )
    );
  }

  function onSizeChange(filterIndex, isFromSize, e) {
    setFilterByList(
      filterByFieldsList.setIn(
        [filterIndex, isFromSize ? 'fromSize' : 'toSize'],
        e.target.value
      )
    );
  }

  function onSubmit(e) {
    e.preventDefault();

    props.dispatch(
      retrieveFilteredUMOs(
        filterByFieldsList
          .map((itemMap) => {
            // if date, then need to convert to appropriate format
            if (itemMap.get('type') === 'dateRange') {
              const fromDate =
                itemMap.get('fromDate') && itemMap.get('fromDate'); //.format(DATE_FORMAT);
              const toDate = itemMap.get('toDate') && itemMap.get('toDate'); //.format(DATE_FORMAT);

              let dateValue;

              if (!fromDate && !toDate) {
                dateValue = '';
              } else if (!fromDate) {
                dateValue = `${DEFAULT_FROM_DATE},${toDate}`;
              } else if (!toDate) {
                dateValue = `${fromDate},${DEFAULT_TO_DATE}`;
              } else {
                dateValue = `${fromDate},${toDate}`;
              }

              return new SearchTerm('DATE_RANGE', dateValue);
            } else if (itemMap.get('type') === 'sizeRange') {
              // if size, then need to convert to appropriate format
              let fromSize = parseInt(itemMap.get('fromSize'));
              let toSize = parseInt(itemMap.get('toSize'));

              // Convert to bytes
              fromSize =
                typeof fromSize === 'number' && !isNaN(fromSize)
                  ? fromSize * 1048576
                  : null;
              toSize =
                typeof toSize === 'number' && !isNaN(toSize)
                  ? toSize * 1048576
                  : null;

              let sizeValue = 'GENERAL.FILE_SIZE,';

              if (!fromSize && !toSize) {
                sizeValue = '';
              } else if (!fromSize) {
                sizeValue += `${DEFAULT_FROM_SIZE},${toSize}`;
              } else if (!toSize) {
                sizeValue += `${fromSize},${DEFAULT_TO_SIZE}`;
              } else {
                sizeValue += `${fromSize},${toSize}`;
              }

              return new SearchTerm('NUMBER_RANGE', sizeValue);
            }
            return itemMap.get('searchTerm');
          })
          .toArray()
      )
    );

    // close the filter dialog
    props.onFilterToggle(false);
  }

  let filterContent;

  const renderedItems = filterByFieldsList.map((itemMap, i) => {
    let field;
    switch (itemMap.get('type')) {
      case 'dateRange':
        field = (
          <div className="umo-filter__date-row">
            <div className="col-md-6 umo-filter__date umo-filter__col-date-from">
              <DatePicker
                className="workspace-detail-view__content-date-edit umo-filter__input"
                bsPrefix="form-control input-sm"
                value={itemMap.get('fromDate') && format(itemMap.get('fromDate'), dateFormat)}
                onChange={(date, e) => onDateChange(date, e, i, true)}
                placeholderText={props.intl.formatMessage({
                  id: 'searchResults.filter.date.from',
                })}
                isClearable
                style={{ width: '160px' }}
                dateFormat={dateFormat}
              />
              <button
                type="button"
                className="react-datepicker__close-icon umo-filter__date-remove"
                aria-label="Close"
                tabIndex="-1"
                onClick={() => onDateChange(null, null, i, true)}
              />
            </div>
            <div className="col-md-6 umo-filter__date umo-filter__col-date-to">
              <DatePicker
                className="workspace-detail-view__content-date-edit umo-filter__input"
                isClearable
                placeholderText={props.intl.formatMessage({
                  id: 'searchResults.filter.date.to',
                })}
                value={itemMap.get('toDate') && format(itemMap.get('toDate'), dateFormat)}
                onChange={(date, e) => onDateChange(date, e, i, false)}
                style={{ width: '160px' }}
              />
              <button
                type="button"
                className="react-datepicker__close-icon umo-filter__date-remove"
                aria-label="Close"
                tabIndex="-1"
                onClick={() => onDateChange(null, null, i, false)}
              />
            </div>
          </div>
        );
        break;
      case 'sizeRange':
        field = (
          <Row>
            <Col md={6} className="umo-filter__col-date-from">
              <input
                type="number"
                className="form-control input-sm umo-filter__input"
                placeholder="From"
                onChange={(e, i) => onSizeChange(i, true, e)}
              />
            </Col>
            <Col md={6} className="umo-filter__col-date-to">
              <input
                type="number"
                className="form-control input-sm umo-filter__input"
                placeholder="To"
                onChange={(e, i) => onSizeChange(i, false, e)}
              />
            </Col>
          </Row>
        );
        break;
      case 'metatag':
        let options = [
          {
            value: '',
            label: props.intl.formatMessage({
              id: 'searchResults.filter.date.all',
            }),
          },
        ];
        itemMap.get('filterOptions').map((optionData, j) => {
          options.push({ value: optionData, label: optionData });
        });
        field = (
          <Select
            className="input-sm umo-filter__input umo-filter__select"
            onChange={(value) => onMetatagChange(i, value)}
            value={{
              label:
                itemMap.getIn(['searchTerm', 'value']) ||
                props.intl.formatMessage({
                  id: 'searchResults.filter.date.all',
                }),
              value: itemMap.getIn(['searchTerm', 'value']) || '',
            }}
            options={options}
            styles={SelectStyles}
          />
        );
        break;
      default:
        break;
    }

    return (
      <Col key={i} md={4}>
        <FormGroup>
          <label className="umo-filter__label">{itemMap.get('label')}</label>
          {field}
        </FormGroup>
      </Col>
    );
  });

  if (error) {
    filterContent = (
      <Alert variant="danger">
        <p>{error.message}</p>
      </Alert>
    );
  } else {
    filterContent = (
      <form onSubmit={onSubmit}>
        <Row>{renderedItems}</Row>

        <Row>
          <Col offset={8} md={4}>
            <ButtonToolbar>
              <Button
                primary
                className="umo-gallery-header__filter-button input-sm"
                type="submit"
              >
                {props.intl.formatMessage({
                  id: 'searchResults.filter.applyFiltersButton',
                })}
              </Button>
              <Button
                className="umo-gallery-header__filter-clear-button btn-outline-info"
                onClick={() => setFilterFields(false)}
              >
                {props.intl.formatMessage({
                  id: 'searchBar.advanced.clearButton',
                })}
              </Button>
            </ButtonToolbar>
          </Col>
        </Row>
      </form>
    );
  }

  return (
    <div
      className={classNames('umo-gallery-header__filter', {
        'umo-gallery-header__filter--show': props.isFilterExpanded,
      })}
    >
      <button
        onClick={() => props.onFilterToggle(false)}
        className="btn umo-filter__close"
      >
        <img src={CloseIcon} className="umo-filter__close-icon" />
      </button>
      <div className="umo-filter">{filterContent}</div>
    </div>
  );
}

UMOFilter.propTypes = {
  isFilterExpanded: PropTypes.bool,
  onFilterToggle: PropTypes.func,
  userCollection: PropTypes.string,
  dispatch: PropTypes.func,
  metatags: PropTypes.instanceOf(Map),
  filterByList: PropTypes.instanceOf(Array),
};

export default injectIntl(UMOFilter);
